import { type FilterImplOptions, Liquid } from 'liquidjs'
import type { TagClass, TagImplOptions } from 'liquidjs/dist/template'
import type PrintCompilerOptions from '#src/interfaces/PrintCompilerOptions'
import errorParser from '#src/error-handling/errorParser'
import PrintCompilerError from '#src/error-handling/PrintCompilerError'

export default class PrintCompiler {
  compiler: Liquid

  constructor(options: PrintCompilerOptions) {
    const { globals, templates, jsTruthy = true } = options.engineConfig
    this.compiler = new Liquid({
      jsTruthy,
      globals,
      templates
    })
  }

  /**
   *  Renders a template with the given context.
   *
   * @param {string} templateName - The name of the template to render.
   * @param {T} [context] - The context object to use for rendering the template.
   * @returns {Promise<string>} - A promise that resolves to the rendered template string.
   * @throws {PrintCompilerError}
   */
  renderTemplate<T extends object = object>(templateName: string, context?: T): Promise<string> {
    return this.compiler.renderFile(templateName, context).catch(error => {
      const message = errorParser.parseMessage(error)

      throw new PrintCompilerError(message, 'PRINT_COMPILER_RENDER_TEMPLATE_ERROR', {
        cause: error
      })
    })
  }

  registerFilter(name: string, filter: FilterImplOptions) {
    this.compiler.registerFilter(name, filter)
  }

  registerTag(name: string, tag: TagClass | TagImplOptions) {
    this.compiler.registerTag(name, tag)
  }

  getTemplateSource(templateName: string): string {
    const templates = this.compiler.options.templates!
    const source = templates[templateName]

    if (typeof source === 'undefined') {
      throw new PrintCompilerError(
        `Template with name "${templateName}" does not exist.`,
        'PRINT_COMPILER_TEMPLATE_NOT_FOUND'
      )
    }

    if (typeof source !== 'string') {
      throw new PrintCompilerError(
        `Template with name "${templateName}" is not a string source.`,
        'PRINT_COMPILER_TEMPLATE_NOT_STRING'
      )
    }

    return source
  }

  updateTemplate(templateName: string, template: string) {
    const templates = this.compiler.options.templates!
    const existingTemplate = templates[templateName]

    if (typeof existingTemplate === 'undefined') {
      throw new PrintCompilerError(
        `Template with name "${templateName}" does not exist and cannot be updated.`,
        'PRINT_COMPILER_UPDATE_TEMPLATE_NOT_FOUND'
      )
    }

    templates[templateName] = template
  }

  updateGlobals(globals: Record<string, string | object>) {
    this.compiler.options.globals = {
      ...this.compiler.options.globals,
      ...globals
    }
  }
}
